import { useEffect, useState } from 'react';

// Named tiers, never numbers, everywhere in code (phase 18 §1).
export type Tier = 'full' | 'laptop' | 'tabletL' | 'tabletP';

/**
 * Tier boundaries, from phase 18 §1. These are the width available to the
 * *app shell*, which equals the viewport in the app and equals the pane width
 * inside the builder's preview (13d) — where the viewport lies.
 */
export function tierFor(width: number): Tier {
  if (width >= 1440) return 'full';
  if (width >= 1200) return 'laptop';
  if (width >= 900) return 'tabletL';
  return 'tabletP';
}

/**
 * The width a CSS media query would see.
 *
 * `window.innerWidth` INCLUDES a classic scrollbar; a `@media (min-width)`
 * test does not. Browsers disagree about when a classic scrollbar exists at
 * all (Edge shows one where Chrome uses an overlay), so measuring with
 * `innerWidth` let the JS tier and the Tailwind breakpoints land on opposite
 * sides of the same boundary, and the nav rail and the page body disagreed
 * about how wide the screen was. `documentElement.clientWidth` is the number
 * the media query uses, in every browser.
 *
 * The `|| innerWidth` fallback is for jsdom, where clientWidth is 0.
 */
function shellWidth(): number {
  if (typeof document === 'undefined') return window.innerWidth;
  return document.documentElement.clientWidth || window.innerWidth;
}

/**
 * The current tier.
 *
 * Phase 18 §2 asks for container queries on the content column. That doesn't
 * work for the tier *decision*: the app nav shrinks faster than the viewport
 * (238 → 186 → 52), so the content column's width is non-monotonic — at 1440px
 * of viewport it is 1202px wide, and at 1439px the nav drops to 186 and it
 * jumps to 1253. A `@container` threshold would step back up into the tier it
 * just left.
 *
 * So the tier is decided once from the shell width and published as
 * `data-tier`; everything downstream is still plain CSS keyed off that
 * attribute, which keeps the "layout CSS can express stays in CSS" rule and
 * keeps working inside a constrained pane.
 *
 * Pass a ref to measure that element instead of the document.
 */
export function useBreakpoint(el?: HTMLElement | null): Tier {
  const [tier, setTier] = useState<Tier>(() =>
    tierFor(typeof window === 'undefined' ? 1680 : shellWidth()),
  );

  useEffect(() => {
    const target = el ?? document.documentElement;

    const measure = () => {
      const w = el ? el.getBoundingClientRect().width : shellWidth();
      setTier((prev) => {
        const next = tierFor(w);
        return next === prev ? prev : next;
      });
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(target);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [el]);

  return tier;
}

export default useBreakpoint;
